/**
 * A link that lands on the right HOST, not just the right path.
 *
 * Use this instead of react-router's <Link> for anything that can point at
 * another product: the room's readers, a subject's room, a briefing's handoffs,
 * and the artifacts shelf — where the destination comes from the server and can
 * be any of the three. See shell/routeShell.ts for what went wrong without it.
 *
 * It degrades to a plain <Link> whenever the target is in this shell already, is
 * part of the shared spine, or there is only one origin (local dev). So it is
 * always safe to reach for; you never have to work out which case you are in.
 */
import { Link, useNavigate } from 'react-router-dom';
import { ReactNode, useCallback } from 'react';
import { crossShellHref } from '../../shell/routeShell';

interface ShellLinkProps {
  to: string;
  children: ReactNode;
  className?: string;
  title?: string;
  onClick?: () => void;
}

export default function ShellLink({
  to,
  children,
  className,
  title,
  onClick,
}: ShellLinkProps) {
  const href = crossShellHref(to, window.location.hostname, window.location.origin);

  // A real navigation, not a router push — the other shell is a different
  // origin and has to boot its own app. No `target="_blank"`: this is "go
  // there", the same as every other link in the app, and a product that opens
  // tabs on your behalf is a product you end up with forty tabs of.
  if (href) {
    return (
      <a href={href} className={className} title={title} onClick={onClick}>
        {children}
      </a>
    );
  }

  return (
    <Link to={to} className={className} title={title} onClick={onClick}>
      {children}
    </Link>
  );
}

/**
 * The same decision for code that navigates rather than renders a link — a
 * button's onClick, a redirect after a create.
 *
 * Returns a function with `navigate`'s shape, so a call site swaps one for the
 * other and nothing else changes.
 */
export function useShellNavigate(): (to: string) => void {
  const navigate = useNavigate();
  return useCallback(
    (to: string) => {
      const href = crossShellHref(to, window.location.hostname, window.location.origin);
      if (href) window.location.assign(href);
      else navigate(to);
    },
    [navigate],
  );
}
